import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsComposerReady } from '~/context/composer/ComposerContext';
import { logInfo } from '~/utilities/loggers';

const COMPOSER_OPEN_GUARD_ACTION = 'composer-open-guard';

// Queue-until-ready shape, mirroring useAccountModalReady. Simpler than that hook
// because Composer has no "some users never need to wait" split — every caller
// (the open button, the isComposerOpened ask on arrival) should wait if not
// ready yet, rather than firing (or dropping) immediately.
export default function useComposerOpenReady() {
  const isReady = useIsComposerReady();
  const pendingRef = useRef<(() => void) | null>(null);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (isReady && pendingRef.current) {
      logInfo(COMPOSER_OPEN_GUARD_ACTION, { outcome: 'deferred-resolved' });

      pendingRef.current();
      pendingRef.current = null;
      setIsPending(false);
    }
  }, [isReady]);

  const whenReady = useCallback(
    (action: () => void) => {
      if (isReady) {
        action();

        return;
      }

      datadogRum.addAction('composer-open-click-deferred', {});
      logInfo(COMPOSER_OPEN_GUARD_ACTION, { outcome: 'deferred' });

      pendingRef.current = action;
      setIsPending(true);
    },
    [isReady]
  );

  return { isPending, isReady, whenReady };
}
